import { adicionarLogo } from "../../services/pdf-logo";
import jsPDF from "jspdf";
import { formatarParaReais, formatDate } from "../../services/utilitarios";
import { Pagamento, StatusPagamento } from "../../services/tipos";
import { montarEnderecoCompleto } from "../fatura/fatura-dados";

export const obterTextoStatus = (status: StatusPagamento): string => {
    switch (status) {
        case StatusPagamento.Pago:
            return "PAGO";
        case StatusPagamento.Pendente:
            return "PENDENTE";
        case StatusPagamento.EmAtraso:
            return "EM ATRASO";
        default:
            return "PENDENTE";
    }
};

export const locacaoCompletaParaRecibo = (locacao: any): boolean =>
    !!locacao && !!locacao.locatario && !!locacao.veiculo;

const enderecoOuNaoInformado = (dados: any) => montarEnderecoCompleto(dados) || "Não informado";

export const desenharConteudoRecibo = (pdf: jsPDF, pagamento: Pagamento, locacao: any, nomeEmpresaFallback: string, posYInicial: number) => {
    const paleta = {
        primaria: "#1A202C",
        secundaria: "#4A5568",
        destaque: "#1F467E",
        borda: "#CBD5E0",
    };

    const dimensoes = {
        larguraPagina: pdf.internal.pageSize.width,
        margem: 15,
    };

    const dataEmissao = new Date().toLocaleDateString("pt-BR");
    const horaEmissao = new Date().toLocaleTimeString("pt-BR");

    let posY = posYInicial;

    pdf.setFontSize(10);
    pdf.setTextColor(paleta.secundaria);
    pdf.text(`Recibo Nº: ${pagamento.id?.substring(0, 8)?.toUpperCase() || "N/A"}`, dimensoes.margem, posY);
    pdf.text(`Data Emissão: ${dataEmissao} às ${horaEmissao}`, dimensoes.larguraPagina - dimensoes.margem, posY, { align: "right" });
    posY += 10;

    pdf.setDrawColor(paleta.borda);
    pdf.line(dimensoes.margem, posY, dimensoes.larguraPagina - dimensoes.margem, posY);
    posY += 8;

    pdf.setFontSize(16);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(paleta.destaque);
    pdf.text(`VALOR: ${formatarParaReais(pagamento.valor)}`, dimensoes.margem, posY);

    const textoStatus = obterTextoStatus(pagamento.status);
    pdf.setFontSize(12);
    pdf.text(textoStatus, dimensoes.larguraPagina - dimensoes.margem, posY, { align: "right" });
    posY += 8;

    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(paleta.secundaria);
    const linhasDescricao = pdf.splitTextToSize(
        `Referente: ${pagamento.descricao || "Pagamento de aluguel"}`,
        dimensoes.larguraPagina - dimensoes.margem * 2
    );
    pdf.text(linhasDescricao, dimensoes.margem, posY);
    posY += linhasDescricao.length * 4.5 + 5.5;

    pdf.setDrawColor(paleta.borda);
    pdf.line(dimensoes.margem, posY, dimensoes.larguraPagina - dimensoes.margem, posY);
    posY += 8;

    pdf.setFontSize(10);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(paleta.destaque);
    pdf.text("RECEBEDOR (EMPRESA)", dimensoes.margem, posY);
    posY += 6;

    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(paleta.primaria);

    const nomeEmpresa = locacao.empresa?.nome || nomeEmpresaFallback;
    pdf.text(nomeEmpresa, dimensoes.margem, posY);
    posY += 5;

    if (locacao.empresa?.documento) {
        pdf.text(`CNPJ/CPF: ${locacao.empresa.documento}`, dimensoes.margem, posY);
        posY += 5;
    }

    if (locacao.empresa) {
        const enderecoEmpresa = enderecoOuNaoInformado(locacao.empresa);
        if (enderecoEmpresa !== "Não informado") {
            const linhasEndereco = pdf.splitTextToSize(`Endereço: ${enderecoEmpresa}`, dimensoes.larguraPagina - dimensoes.margem * 2);
            pdf.text(linhasEndereco, dimensoes.margem, posY);
            posY += linhasEndereco.length * 4.5;
        }
    }
    posY += 6;

    pdf.setFontSize(10);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(paleta.destaque);
    pdf.text("PAGADOR (LOCATÁRIO)", dimensoes.margem, posY);
    posY += 6;

    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(paleta.primaria);

    pdf.text(locacao.locatario.nome, dimensoes.margem, posY);
    posY += 5;

    pdf.text(`Documento: ${locacao.locatario.documento}`, dimensoes.margem, posY);
    posY += 5;

    const enderecoLocatario = enderecoOuNaoInformado(locacao.locatario);
    if (enderecoLocatario !== "Não informado") {
        const linhasEndereco = pdf.splitTextToSize(`Endereço: ${enderecoLocatario}`, dimensoes.larguraPagina - dimensoes.margem * 2);
        pdf.text(linhasEndereco, dimensoes.margem, posY);
        posY += linhasEndereco.length * 4.5;
    }

    if (locacao.locatario.telefone) {
        pdf.text(`Telefone: ${locacao.locatario.telefone}`, dimensoes.margem, posY);
        posY += 5;
    }

    if (locacao.locatario.email) {
        pdf.text(`Email: ${locacao.locatario.email}`, dimensoes.margem, posY);
        posY += 5;
    }
    posY += 6;

    pdf.setFontSize(10);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(paleta.destaque);
    pdf.text("VEÍCULO", dimensoes.margem, posY);
    posY += 6;

    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(paleta.primaria);

    const infoVeiculo = [
        { label: "Veículo:", value: `${locacao.veiculo.marca} ${locacao.veiculo.modelo}` },
        { label: "Placa:", value: locacao.veiculo.placa || "N/A" },
        { label: "Ano:", value: locacao.veiculo.ano?.toString() || "N/A" },
    ];

    if (locacao.veiculo.cor) {
        infoVeiculo.push({ label: "Cor:", value: locacao.veiculo.cor });
    }

    infoVeiculo.forEach((info) => {
        pdf.text(info.label, dimensoes.margem, posY);
        pdf.text(info.value, dimensoes.margem + 20, posY);
        posY += 5;
    });
    posY += 6;

    pdf.setFontSize(10);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(paleta.destaque);
    pdf.text("DADOS DO PAGAMENTO", dimensoes.margem, posY);
    posY += 6;

    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(paleta.primaria);

    const infoPagamento = [
        { label: "Valor:", value: formatarParaReais(pagamento.valor) },
        { label: "Vencimento:", value: formatDate(pagamento.dataPrevista.toString()) },
        { label: "Data Pagamento:", value: pagamento.dataPagamentoRealizado ? formatDate(pagamento.dataPagamentoRealizado) : "Não realizado" },
        { label: "Status:", value: textoStatus },
    ];

    infoPagamento.forEach((info) => {
        pdf.text(info.label, dimensoes.margem, posY);
        pdf.text(info.value, dimensoes.margem + 35, posY);
        posY += 5;
    });
    posY += 15;

    const larguraAssinatura = (dimensoes.larguraPagina - dimensoes.margem * 2 - 20) / 2;

    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    pdf.line(dimensoes.margem, posY, dimensoes.margem + larguraAssinatura, posY);
    pdf.text(nomeEmpresa, dimensoes.margem, posY + 5);
    pdf.setFontSize(8);
    pdf.setTextColor(paleta.secundaria);
    pdf.text("Empresa Locadora", dimensoes.margem, posY + 9);

    pdf.setFontSize(9);
    pdf.setTextColor(paleta.primaria);
    pdf.line(dimensoes.margem + larguraAssinatura + 20, posY, dimensoes.larguraPagina - dimensoes.margem, posY);
    pdf.text(locacao.locatario.nome, dimensoes.margem + larguraAssinatura + 20, posY + 5);
    pdf.setFontSize(8);
    pdf.setTextColor(paleta.secundaria);
    pdf.text("Locatário", dimensoes.margem + larguraAssinatura + 20, posY + 9);

    const rodapeY = pdf.internal.pageSize.height - 15;
    pdf.setFontSize(7);
    pdf.setTextColor(paleta.secundaria);
    pdf.text("Este recibo foi gerado automaticamente pelo sistema LocaSmartPro", dimensoes.larguraPagina / 2, rodapeY, { align: "center" });
};

export const desenharReciboPagamento = (pdf: jsPDF, pagamento: Pagamento, locacao: any, nomeEmpresaFallback: string, logo: HTMLImageElement | null) => {
    if (logo) {
        const larguraPagina = pdf.internal.pageSize.width;
        adicionarLogo(pdf, logo, { x: larguraPagina - 55, y: 10, largura: 40, altura: 25, alinhar: "direita" });
    }

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);
    pdf.setTextColor("#1A202C");
    pdf.text("RECIBO DE PAGAMENTO", 15, 20);

    desenharConteudoRecibo(pdf, pagamento, locacao, nomeEmpresaFallback, logo ? 40 : 30);
};

export interface ReciboEmLote {
    pagamento: Pagamento;
    locacao: any;
}

export interface ReciboUnificado {
    locacao: any;
    pagamentos: Pagamento[];
}

export const agruparRecibosPorCliente = (recibos: ReciboEmLote[]): ReciboUnificado[] => {
    const grupos = new Map<string, ReciboUnificado>();
    recibos.forEach(r => {
        const locatario = r.locacao?.locatario ?? {};
        const chave = String(locatario.documento || locatario.nome || "").trim().toLowerCase();
        const grupo = grupos.get(chave);
        if (grupo) grupo.pagamentos.push(r.pagamento);
        else grupos.set(chave, { locacao: r.locacao, pagamentos: [r.pagamento] });
    });
    return Array.from(grupos.values()).map(g => ({
        ...g,
        pagamentos: [...g.pagamentos].sort((a, b) => new Date(a.dataPrevista).getTime() - new Date(b.dataPrevista).getTime()),
    }));
};

export const totalDosPagamentos = (pagamentos: Pagamento[]): number =>
    pagamentos.reduce((soma, p) => soma + (Number(p.valor) || 0), 0);

export const desenharReciboUnificado = (pdf: jsPDF, recibo: ReciboUnificado, nomeEmpresaFallback: string, logo: HTMLImageElement | null) => {
    const { locacao, pagamentos } = recibo;
    const paleta = { primaria: "#1A202C", secundaria: "#4A5568", destaque: "#1F467E", borda: "#CBD5E0" };
    const larguraPagina = pdf.internal.pageSize.width;
    const margem = 15;
    const largura = larguraPagina - margem * 2;

    if (logo) adicionarLogo(pdf, logo, { x: larguraPagina - 55, y: 10, largura: 40, altura: 25, alinhar: "direita" });

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);
    pdf.setTextColor(paleta.primaria);
    pdf.text("RECIBO DE PAGAMENTO", margem, 20);

    let posY = logo ? 40 : 30;
    const primeiro = pagamentos[0];
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor(paleta.secundaria);
    pdf.text(`Recibo Nº: ${primeiro?.id?.substring(0, 8)?.toUpperCase() || "N/A"}`, margem, posY);
    pdf.text(`Data Emissão: ${new Date().toLocaleDateString("pt-BR")} às ${new Date().toLocaleTimeString("pt-BR")}`, larguraPagina - margem, posY, { align: "right" });
    posY += 10;

    pdf.setDrawColor(paleta.borda);
    pdf.line(margem, posY, larguraPagina - margem, posY);
    posY += 8;

    pdf.setFontSize(16);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(paleta.destaque);
    pdf.text(`VALOR TOTAL: ${formatarParaReais(totalDosPagamentos(pagamentos))}`, margem, posY);
    pdf.setFontSize(12);
    pdf.text("PAGO", larguraPagina - margem, posY, { align: "right" });
    posY += 8;

    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(paleta.secundaria);
    pdf.text(`Referente a ${pagamentos.length} pagamento(s) recebido(s), relacionados abaixo.`, margem, posY);
    posY += 8;

    pdf.setDrawColor(paleta.borda);
    pdf.line(margem, posY, larguraPagina - margem, posY);
    posY += 8;

    const secao = (titulo: string) => {
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(paleta.destaque);
        pdf.text(titulo, margem, posY);
        posY += 6;
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(paleta.primaria);
    };

    secao("RECEBEDOR (EMPRESA)");
    const nomeEmpresa = locacao?.empresa?.nome || nomeEmpresaFallback;
    pdf.text(nomeEmpresa, margem, posY);
    posY += 5;
    if (locacao?.empresa?.documento) {
        pdf.text(`CNPJ/CPF: ${locacao.empresa.documento}`, margem, posY);
        posY += 5;
    }
    const enderecoEmpresa = montarEnderecoCompleto(locacao?.empresa);
    if (enderecoEmpresa) {
        const linhas = pdf.splitTextToSize(`Endereço: ${enderecoEmpresa}`, largura);
        pdf.text(linhas, margem, posY);
        posY += linhas.length * 4.5;
    }
    posY += 6;

    secao("PAGADOR (LOCATÁRIO)");
    const locatario = locacao?.locatario ?? {};
    pdf.text(locatario.nome || "Não informado", margem, posY);
    posY += 5;
    if (locatario.documento) {
        pdf.text(`Documento: ${locatario.documento}`, margem, posY);
        posY += 5;
    }
    const enderecoLocatario = montarEnderecoCompleto(locatario);
    if (enderecoLocatario) {
        const linhas = pdf.splitTextToSize(`Endereço: ${enderecoLocatario}`, largura);
        pdf.text(linhas, margem, posY);
        posY += linhas.length * 4.5;
    }
    if (locatario.telefone) {
        pdf.text(`Telefone: ${locatario.telefone}`, margem, posY);
        posY += 5;
    }
    if (locatario.email) {
        pdf.text(`Email: ${locatario.email}`, margem, posY);
        posY += 5;
    }
    posY += 6;

    secao("PAGAMENTOS RECEBIDOS");
    const colunas = { vencimento: margem, pagoEm: margem + 26, descricao: margem + 52, valor: larguraPagina - margem };
    const larguraDescricao = colunas.valor - colunas.descricao - 30;
    pdf.setFontSize(8);
    pdf.setTextColor(paleta.secundaria);
    pdf.text("Vencimento", colunas.vencimento, posY);
    pdf.text("Pago em", colunas.pagoEm, posY);
    pdf.text("Referente", colunas.descricao, posY);
    pdf.text("Valor", colunas.valor, posY, { align: "right" });
    posY += 2;
    pdf.setDrawColor(paleta.borda);
    pdf.line(margem, posY, larguraPagina - margem, posY);
    posY += 5;

    const alturaRodape = pdf.internal.pageSize.height - 45;
    pagamentos.forEach(p => {
        const linhas = pdf.splitTextToSize(p.descricao || "Pagamento de aluguel", larguraDescricao) as string[];
        const alturaLinha = Math.max(linhas.length * 4.2, 5);
        if (posY + alturaLinha > alturaRodape) {
            pdf.addPage();
            posY = 20;
        }
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(paleta.primaria);
        pdf.text(formatDate(p.dataPrevista.toString()), colunas.vencimento, posY);
        pdf.text(p.dataPagamentoRealizado ? formatDate(p.dataPagamentoRealizado) : "-", colunas.pagoEm, posY);
        pdf.text(linhas, colunas.descricao, posY);
        pdf.text(formatarParaReais(p.valor), colunas.valor, posY, { align: "right" });
        posY += alturaLinha + 1.5;
    });
    pdf.setDrawColor(paleta.borda);
    pdf.line(margem, posY, larguraPagina - margem, posY);
    posY += 6;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.text("Total recebido:", colunas.valor - 40, posY, { align: "right" });
    pdf.text(formatarParaReais(totalDosPagamentos(pagamentos)), colunas.valor, posY, { align: "right" });
    posY += 18;

    if (posY + 20 > pdf.internal.pageSize.height - 20) {
        pdf.addPage();
        posY = 30;
    }
    const larguraAssinatura = (largura - 20) / 2;
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(paleta.primaria);
    pdf.line(margem, posY, margem + larguraAssinatura, posY);
    pdf.text(nomeEmpresa, margem, posY + 5);
    pdf.setFontSize(8);
    pdf.setTextColor(paleta.secundaria);
    pdf.text("Empresa Locadora", margem, posY + 9);

    pdf.setFontSize(9);
    pdf.setTextColor(paleta.primaria);
    pdf.line(margem + larguraAssinatura + 20, posY, larguraPagina - margem, posY);
    pdf.text(locatario.nome || "", margem + larguraAssinatura + 20, posY + 5);
    pdf.setFontSize(8);
    pdf.setTextColor(paleta.secundaria);
    pdf.text("Locatário", margem + larguraAssinatura + 20, posY + 9);

    pdf.setFontSize(7);
    pdf.text("Este recibo foi gerado automaticamente pelo sistema LocaSmartPro", larguraPagina / 2, pdf.internal.pageSize.height - 15, { align: "center" });
};

export const gerarPdfRecibosUnificados = (grupos: ReciboUnificado[], nomeEmpresaFallback: string, logo: HTMLImageElement | null): jsPDF => {
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    grupos.forEach((grupo, indice) => {
        if (indice > 0) pdf.addPage();
        desenharReciboUnificado(pdf, grupo, nomeEmpresaFallback, logo);
    });
    return pdf;
};

export const gerarPdfRecibos = (recibos: ReciboEmLote[], nomeEmpresaFallback: string, logo: HTMLImageElement | null): jsPDF => {
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    recibos.forEach((recibo, indice) => {
        if (indice > 0) pdf.addPage();
        desenharReciboPagamento(pdf, recibo.pagamento, recibo.locacao, nomeEmpresaFallback, logo);
    });
    return pdf;
};
